/* @flow */

import * as React from 'react';
import { connect } from 'react-redux';
import styled from '@emotion/styled';
import { borderWidth, padding, rgba, tint } from 'polished';
import pluralize from 'pluralize';

import PlayerAvatar from 'app/components/challenge/PlayerAvatar';
import PlayerBar from 'app/components/challenge/PlayerBar';
import Spinner from 'app/components/commons/Spinner';
import { playerSwapSubmit } from 'app/actions/playerSwapActions';
import playerSwapSelectedPlayerSelector from 'app/selectors/playerSwapSelectedPlayerSelector';
import PlayerSwapSuccess from './PlayerSwapSuccess';

type Props = {
  errorMessages?: Array<string>,
  isRacing: boolean,
  onCancel: () => void,
  onReset: () => void,
  onSubmit: () => void,
  salaryLoss?: string,
  selectedPlayer?: Object,
  submitting: boolean,
  swapPlayer?: Object,
  entryCount: number
};

const defaultProps = {
  errorMessages: [],
  salaryLoss: undefined,
  selectedPlayer: {},
  swapPlayer: {}
};

const Wrapper = styled.div({
  paddingTop: 5,
  paddingBottom: 10
});

const Headline = styled.div(({ theme }) => ({
  fontSize: '1.25rem',
  fontWeight: theme.fontWeight.medium,
  marginBottom: '1rem',
  textAlign: 'center'
}));

const Final = styled.div(({ theme }) => ({
  fontWeight: theme.fontWeight.medium,
  marginBottom: '1rem',
  paddingTop: '1rem',
  textAlign: 'center'
}));

const AvatarWrapper = styled.div({
  flexBasis: 'auto',
  flexGrow: 0,
  flexShrink: 0,
  paddingRight: 5
});

const Indicator = styled.div(({ theme }) => ({
  alignItems: 'center',
  borderColor: theme.color.basis5,
  borderStyle: 'solid',
  ...borderWidth(0, 2, 0, 0),
  display: 'flex',
  flexBasis: '7rem',
  flexGrow: 0,
  flexShrink: 0,
  marginRight: '1rem'
}));

const CancelButton = styled('button')(({ theme }) => ({
  color: theme.color.basis4,
  display: 'flex',
  margin: '0 auto',
  paddingTop: theme.spacing.small,
  fontSize: '0.833rem',
  textTransform: 'uppercase',
  '&:hover': {
    color: theme.color.basis2
  }
}));

const SelectionLabel = styled.span(
  {
    alignItems: 'center',
    display: 'flex',
    fontSize: '1.125rem',
    paddingLeft: '2rem',
    textTransform: 'uppercase'
  },
  ({ theme, type }) =>
    ({
      primary: {
        color: theme.color.primary
      },
      secondary: {
        color: theme.color.secondary
      }
    }[type])
);

const SelectionIcon = styled.span({
  display: 'inline-block',
  fontSize: '1.5rem',
  marginRight: '0.5em'
});

const PlayerRow = styled.div({
  display: 'flex',
  ...padding(10, 0)
});

const SubmitButton = styled('button')(({ theme }) => ({
  backgroundColor: theme.color.success,
  borderRadius: theme.borderRadius.medium,
  color: 'white',
  display: 'flex',
  justifyContent: 'center',
  lineHeight: 1.6, // same height as spinner
  margin: '0 auto',
  minWidth: '8.5rem',
  ...padding('0.25rem', '1rem'),
  textAlign: 'center',
  textTransform: 'uppercase',
  '&:hover': {
    backgroundColor: tint(0.75, theme.color.success)
  }
}));

const ErrorMessage = styled.div(({ theme }) => ({
  backgroundColor: rgba(theme.color.danger, 0.1),
  borderColor: theme.color.danger,
  borderStyle: 'solid',
  ...borderWidth(4),
  ...padding('0.5rem', 0, '0.5rem', '0.75rem')
}));

const SalaryLoss = styled.p(({ theme }) => ({
  color: theme.color.danger
}));

const PlayerSwapConfirm = ({
  errorMessages,
  isRacing,
  onCancel,
  onReset,
  onSubmit,
  salaryLoss,
  selectedPlayer,
  submitting,
  swapPlayer,
  entryCount
}: Props) => {
  const [hasSubmitted, setHasSubmitted] = React.useState(false);

  const handleSubmit = () => {
    onSubmit();
    setHasSubmitted(true);
  };

  if (hasSubmitted && !submitting && errorMessages.length === 0) {
    return <PlayerSwapSuccess onReset={onReset} />;
  }

  return (
    <Wrapper>
      <Headline>Confirm Player Swap</Headline>
      {errorMessages.length > 0 && (
        <ErrorMessage>
          {errorMessages.map(msg => (
            <div>{msg}</div>
          ))}
        </ErrorMessage>
      )}
      <PlayerRow>
        <Indicator>
          <SelectionLabel type="primary">
            <SelectionIcon className="ion-android-arrow-dropright-circle" /> Out
          </SelectionLabel>
        </Indicator>
        <AvatarWrapper>
          <PlayerAvatar
            image={isRacing ? selectedPlayer.riderImage : selectedPlayer.imageURI}
            fallbackText={selectedPlayer.initials}
          />
        </AvatarWrapper>
        <PlayerBar
          awayTeam={selectedPlayer.awayAbbr}
          homeTeam={selectedPlayer.homeAbbr}
          gameOrder={selectedPlayer.gameOrder}
          isHome={selectedPlayer.isHome}
          isRacing={isRacing}
          isScratched={selectedPlayer.isScratched}
          name={selectedPlayer.name}
          place={selectedPlayer.place}
          position={selectedPlayer.positionAbbr}
          rider={selectedPlayer.rider}
          trainer={selectedPlayer.trainer}
          win={selectedPlayer.win}
        />
      </PlayerRow>
      <PlayerRow>
        <Indicator>
          <SelectionLabel type="secondary">
            <SelectionIcon className="ion-android-arrow-dropleft-circle" /> In
          </SelectionLabel>
        </Indicator>
        <AvatarWrapper>
          <PlayerAvatar
            image={isRacing ? swapPlayer.riderImage : swapPlayer.imageURI}
            fallbackText={swapPlayer.initials}
          />
        </AvatarWrapper>
        <PlayerBar
          awayTeam={swapPlayer.awayAbbr}
          homeTeam={swapPlayer.homeAbbr}
          gameOrder={swapPlayer.gameOrder}
          isHome={swapPlayer.isHome}
          isRacing={isRacing}
          isScratched={swapPlayer.isScratched}
          name={swapPlayer.name}
          place={swapPlayer.place}
          position={selectedPlayer.positionAbbr} // use selected player's position
          rider={swapPlayer.rider}
          trainer={swapPlayer.trainer}
          win={swapPlayer.win}
        />
      </PlayerRow>
      <Final>
        <p>
          Swap {selectedPlayer.preferredName} with {swapPlayer.preferredName} in {entryCount}{' '}
          upcoming {pluralize('entry', entryCount)}?
        </p>
        {salaryLoss && <SalaryLoss>Salary loss: {salaryLoss}</SalaryLoss>}
      </Final>
      <SubmitButton type="button" onClick={handleSubmit}>
        {submitting ? <Spinner kind="light" center /> : <span>Confirm</span>}
      </SubmitButton>
      <CancelButton type="button" onClick={onCancel}>
        Cancel
      </CancelButton>
    </Wrapper>
  );
};

const connector = connect(
  state => {
    const {
      hardErrorMessages,
      selectedGroupId,
      selectedPosition,
      submitting,
      swapPlayerId
    } = state.playerSwap;

    const selectedPlayer = playerSwapSelectedPlayerSelector(state);

    return {
      errorMessages: hardErrorMessages,
      selectedGroupId,
      selectedPlayer,
      selectedPosition,
      submitting,
      swapPlayer: state.playerSwap.playerPool[swapPlayerId],
      entryCount: selectedPlayer.entryCount
    };
  },
  {
    onSubmit: playerSwapSubmit
  },
  (stateProps, dispatchProps, ownProps) => ({
    ...ownProps,
    ...stateProps,
    ...dispatchProps,
    onSubmit() {
      const { selectedGroupId, selectedPlayer, selectedPosition, swapPlayer } = stateProps;

      dispatchProps.onSubmit({
        lineups: selectedPlayer.lineups,
        isTeamSelected: !!selectedPlayer.isTeam,
        isTeamToSwap: !!swapPlayer.isTeam,
        selectedPlayerId: selectedPlayer.id,
        selectedPositionId: selectedPosition,
        slateId: selectedGroupId,
        swapPlayerId: swapPlayer.id,
        swapPositionId: selectedPosition
      });
    }
  })
);

PlayerSwapConfirm.defaultProps = defaultProps;

export default connector(PlayerSwapConfirm);
